import { AdminGame, GameKind, GameMode, WinMode } from '@/lib-backend';

/**
 * The createGame / updateGame responses do not re-resolve the template's
 * GameKind (only GameKindId), so the resolved object would be missing on a
 * freshly created/updated game — blanking out WinMode-derived UI (e.g. the
 * art-generation "no compatible site template" guard). Fill it from a known
 * resolved GameKind (the selected template, or the game's previous state).
 */
export function withResolvedGameKind(
    game: AdminGame,
    fallback: GameKind | null | undefined,
): AdminGame {
    if (game.Template.Template.GameKind || !fallback) return game;
    return {
        ...game,
        Template: {
            ...game.Template,
            Template: { ...game.Template.Template, GameKind: fallback },
        },
    };
}

export function effectiveTitle(g: AdminGame): string {
    return g.Overrides.Title ?? g.Template.Template.Title;
}

export function effectiveDescription(g: AdminGame): string {
    return g.Overrides.Description ?? g.Template.Template.Description;
}

export function effectiveIconUrl(g: AdminGame): string {
    return g.Overrides.IconUrl ?? g.Template.Template.IconUrl;
}

export function effectiveBannerUrl(g: AdminGame): string {
    return g.Overrides.BannerUrl ?? g.Template.Template.BannerUrl;
}

export function effectiveImages(g: AdminGame): string[] {
    return g.Overrides.Images ?? g.Template.Template.Images;
}

export function effectiveIsActive(g: AdminGame): boolean {
    return g.Overrides.IsActive ?? g.Template.Template.IsActive;
}

export function effectiveIsFeatured(g: AdminGame): boolean {
    return g.Overrides.IsFeatured ?? g.Template.Template.IsFeatured;
}

export function gameKindName(g: AdminGame): string {
    return g.Template.Template.GameKind?.Name ?? g.Template.Template.GameKindId;
}

export function gameModeLabel(mode: GameMode | undefined | null): string {
    switch (mode) {
        case GameMode.SinglePlayer: return 'Single player';
        case GameMode.MultiPlayerAsync: return 'Multiplayer (async)';
        case GameMode.Rolls: return 'Rolls';
        case GameMode.Unknown: return 'Unknown';
        default: return '—';
    }
}

export function winModeLabel(mode: WinMode | undefined | null): string {
    switch (mode) {
        case WinMode.SkillBased: return 'Skill-based';
        case WinMode.ChanceBased: return 'Chance-based';
        case WinMode.Unknown: return 'Unknown';
        default: return '—';
    }
}
